import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Minus, Plus, Trash2, X, AlertTriangle } from 'lucide-react';
import SubstitutionPopover from '../../components/SubstitutionPopover';
import { fmtCurrency } from '../../lib/localeUtils';

/**
 * One cart line. `compact` renders the mobile variant (no clinical extras,
 * tighter controls); the desktop variant adds expiry / refill warnings and
 * the generic-substitution popover.
 */
export const POSCartItem = ({
  item, expiryWarning, refillWarning, patientId, compact = false,
  onUpdateQuantity, onQuantityInput, onQuantityBlur, onRemove, onSwap,
}) => {
  const warned = !compact && !!expiryWarning?.has_warning;
  const prefix = compact ? 'mobile-' : '';
  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 min-w-0 cart-item-enter ${warned ? 'bg-red-500/15 border border-red-500/40 rounded-lg p-2' : ''}`}
      data-testid={`${prefix}cart-item-${item.product_id}`}
    >
      <div className="col-span-3 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium break-words" data-testid={`${prefix}cart-name-${item.product_id}`}>{item.product_name}</p>
          {!compact && item.fromPrescription && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 text-primary border-primary/50">Rx</Badge>
          )}
          {warned && (
            <Badge variant="destructive" className="text-[9px] px-1 py-0 gap-0.5">
              <AlertTriangle className="h-2.5 w-2.5" /> EXPIRY
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {fmtCurrency(item.unit_price)}{compact ? '' : ' each'}
        </p>
        {!compact && refillWarning && (
          <p className="text-[10px] text-amber-700 mt-0.5 font-medium" data-testid={`refill-warning-${item.product_id}`}>
            {refillWarning}
          </p>
        )}
        {!compact && (
          <div className="mt-1">
            <SubstitutionPopover item={item} patientId={patientId} saleId={null} onSwap={onSwap} />
          </div>
        )}
        {warned && (
          <p className="text-[10px] text-red-500 mt-0.5 font-medium">
            {expiryWarning.expired_batches.length} expired batch(es) still on shelf!
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="icon" className="h-7 w-7"
          onClick={() => onUpdateQuantity(item.product_id, -1)}
          data-testid={`${prefix}qty-minus-${item.product_id}`}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <Input
          type="number"
          min={1}
          max={item.max_stock}
          value={item.quantity}
          onChange={(e) => onQuantityInput(item.product_id, e.target.value)}
          onBlur={(e) => onQuantityBlur(item.product_id, e.target.value)}
          className={`${compact ? 'w-12' : 'w-14'} h-7 text-center text-sm font-semibold p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
          data-testid={`${prefix}qty-input-${item.product_id}`}
        />
        <Button
          variant="outline" size="icon" className="h-7 w-7"
          onClick={() => onUpdateQuantity(item.product_id, 1)}
          data-testid={`${prefix}qty-plus-${item.product_id}`}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      <p className="text-sm font-semibold text-right" data-testid={`${prefix}cart-line-total-${item.product_id}`}>
        {fmtCurrency(item.quantity * item.unit_price)}
      </p>
      <Button
        variant="ghost" size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={() => onRemove(item.product_id)}
        data-testid={`${prefix}remove-item-${item.product_id}`}
      >
        {compact ? <X className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
};
